import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Company } from "../lib/utils";
import { formatDate, formatRelativeTime, truncate } from "../lib/utils";
import { Card, Badge, Button, LoadingSpinner, EmptyState, Modal, Input, Label } from "../components/ui";

export function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, [query, page]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await api.companies.list({ q: query, limit: 20 });
      if (page === 0) {
        setCompanies(data.companies);
      } else {
        setCompanies(prev => [...prev, ...data.companies]);
      }
      setHasMore(data.companies.length === 20);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    loadCompanies();
  };

  const handleViewDetail = (company: Company) => {
    setSelectedCompany(company);
    setShowDetail(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
          <p className="text-slate-500 mt-1">Discovered and tracked companies</p>
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search companies..."
            className="input w-64"
          />
          <Button type="submit" icon="search">Search</Button>
        </form>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50 text-red-700">
          {error}
        </div>
      )}

      {loading && companies.length === 0 ? (
        <LoadingSpinner size="lg" />
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Industry</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Size</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Contacts</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 truncate max-w-xs">{company.name}</p>
                        {company.website && (
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm truncate block max-w-xs">
                            {company.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{company.industry || "Not specified"}</td>
                      <td className="px-4 py-3 text-slate-700">{company.location || "Not specified"}</td>
                      <td className="px-4 py-3 text-slate-700">{company.size || "Not specified"}</td>
                      <td className="px-4 py-3">
                        <Badge className="bg-slate-100 text-slate-700">{company.source}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {company.contact_emails.slice(0, 2).map((email, i) => (
                            <Badge key={i} className="bg-blue-50 text-blue-700 text-xs">{email}</Badge>
                          ))}
                          {company.contact_emails.length > 2 && (
                            <Badge className="bg-slate-100 text-slate-600 text-xs">+{company.contact_emails.length - 2} more</Badge>
                          )}
                          {company.contact_phones.slice(0, 1).map((phone, i) => (
                            <Badge key={i} className="bg-green-50 text-green-700 text-xs">{phone}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="ghost" onClick={() => handleViewDetail(company)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {hasMore && (
              <div className="p-4 border-t border-slate-200">
                <Button onClick={() => setPage(p => p + 1)} className="w-full" variant="secondary">
                  Load More
                </Button>
              </div>
            )}
            
            {companies.length === 0 && !loading && (
              <EmptyState 
                icon="Building2" 
                title="No companies found" 
                description={query ? "Try a different search term." : "No companies discovered yet. Run a fetch cycle to find companies."} 
              />
            )}
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="Company Details" size="lg">
        {selectedCompany && (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900">{selectedCompany.name}</h3>
                {selectedCompany.website && (
                  <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 text-sm">
                    {selectedCompany.website}
                  </a>
                )}
              </div>
              <Badge className="bg-slate-100 text-slate-700">{selectedCompany.source}</Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Industry</Label>
                <p className="text-slate-900">{selectedCompany.industry || "Not specified"}</p>
              </div>
              <div>
                <Label>Size</Label>
                <p className="text-slate-900">{selectedCompany.size || "Not specified"}</p>
              </div>
              <div>
                <Label>Location</Label>
                <p className="text-slate-900">{selectedCompany.location || "Not specified"}</p>
              </div>
              <div>
                <Label>Last Enriched</Label>
                <p className="text-slate-900">{formatRelativeTime(selectedCompany.last_enriched)}</p>
              </div>
            </div>
            
            {selectedCompany.description && (
              <div>
                <Label>Description</Label>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedCompany.description}</p>
              </div>
            )}
            
            {selectedCompany.tech_stack && selectedCompany.tech_stack.length > 0 && (
              <div>
                <Label>Tech Stack</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedCompany.tech_stack.map((tech: string) => (
                    <Badge key={tech} className="bg-primary-50 text-primary-700">{tech}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {selectedCompany.pain_points && selectedCompany.pain_points.length > 0 && (
              <div>
                <Label>Pain Points</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedCompany.pain_points.map((point: string) => (
                    <Badge key={point} className="bg-amber-50 text-amber-700">{point}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {(selectedCompany.contact_emails.length > 0 || selectedCompany.contact_phones.length > 0) && (
              <div>
                <Label>Contacts</Label>
                <div className="space-y-2">
                  {selectedCompany.contact_emails.map((email, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge className="bg-blue-50 text-blue-700">Email</Badge>
                      <a href={`mailto:${email}`} className="text-primary-600 hover:text-primary-700">{email}</a>
                    </div>
                  ))}
                  {selectedCompany.contact_phones.map((phone, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Badge className="bg-green-50 text-green-700">Phone</Badge>
                      <a href={`tel:${phone}`} className="text-primary-600 hover:text-primary-700">{phone}</a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}